import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { WalletState, WalletType, NetworkConfig, WalletAccount, SUPPORTED_NETWORKS } from '../../types/wallet';
import { MetaMaskService } from '../../services/MetaMaskService';
import { WalletConnectService } from '../../services/WalletConnectService';
import { toast } from 'sonner';

interface WalletContextType extends WalletState {
  connectWallet: (type: WalletType) => Promise<void>;
  disconnectWallet: () => Promise<void>;
  switchNetwork: (chainId: number) => Promise<void>;
  signMessage: (message: string) => Promise<string>;
  sendTransaction: (params: any) => Promise<string>;
}

const WalletContext = createContext<WalletContextType | null>(null);

type WalletAction =
  | { type: 'SET_CONNECTING'; payload: boolean }
  | { type: 'SET_CONNECTED'; payload: { account: WalletAccount; network: NetworkConfig; walletType: WalletType } }
  | { type: 'SET_DISCONNECTED' }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'SET_NETWORK'; payload: NetworkConfig }
  | { type: 'SET_ACCOUNT'; payload: WalletAccount };

const initialState: WalletState = {
  isConnected: false,
  account: null,
  network: null,
  walletType: null,
  isConnecting: false,
  error: null,
};

function walletReducer(state: WalletState, action: WalletAction): WalletState {
  switch (action.type) {
    case 'SET_CONNECTING':
      return { ...state, isConnecting: action.payload, error: null };
    case 'SET_CONNECTED':
      return {
        ...state,
        isConnected: true,
        account: action.payload.account,
        network: action.payload.network,
        walletType: action.payload.walletType,
        isConnecting: false,
        error: null,
      };
    case 'SET_DISCONNECTED':
      return { ...initialState };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isConnecting: false };
    case 'SET_NETWORK':
      return { ...state, network: action.payload };
    case 'SET_ACCOUNT':
      return { ...state, account: action.payload };
    default:
      return state;
  }
}

export function WalletProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(walletReducer, initialState);

  const connectWallet = async (type: WalletType) => {
    console.log('WalletProvider: connectWallet called with type:', type);

    try {
      dispatch({ type: 'SET_CONNECTING', payload: true });

      let account: WalletAccount;
      let network: NetworkConfig;

      const walletType = String(type).toLowerCase();
      console.log('Wallet type normalized:', walletType);

      if (walletType === 'metamask' || walletType === WalletType.METAMASK) {
        console.log('Connecting to MetaMask...');

        const metaMask = new MetaMaskService();
        const accounts = await metaMask.connect();
        const chainId = await metaMask.getChainId();
        const balance = await metaMask.getBalance(accounts[0]);

        account = {
          address: accounts[0],
          balance,
          chainId: parseInt(chainId, 16),
        };

        network = SUPPORTED_NETWORKS[account.chainId] || SUPPORTED_NETWORKS[1];
        console.log('MetaMask connected successfully!');

      } else if (walletType === 'thor_wallet' || walletType === WalletType.THOR_WALLET) {
        console.log('Connecting to THOR Wallet...');

        // THOR Wallet uses EIP-1193 provider interface (same as MetaMask)
        if (typeof window === 'undefined' || !window.ethereum) {
          throw new Error('No wallet provider found. Please install THOR Wallet or MetaMask.');
        }

        // Check for THOR Wallet specific provider
        let provider = window.ethereum;

        // If multiple providers exist, try to find THOR Wallet
        if (window.ethereum.providers) {
          const thorProvider = window.ethereum.providers.find((p: any) => p.isThorWallet);
          if (thorProvider) {
            provider = thorProvider;
          }
        }

        // Check if THOR Wallet is available
        if (provider.isThorWallet) {
          console.log('THOR Wallet detected!');
        } else {
          console.log('THOR Wallet not detected, using available provider...');
        }

        const accounts = await provider.request({ method: 'eth_requestAccounts' });

        if (!accounts || accounts.length === 0) {
          throw new Error('No accounts found in THOR Wallet');
        }

        const chainIdHex = await provider.request({ method: 'eth_chainId' });
        const balance = await provider.request({
          method: 'eth_getBalance',
          params: [accounts[0], 'latest'],
        });

        account = {
          address: accounts[0],
          balance: balance || '0x0',
          chainId: parseInt(chainIdHex, 16),
        };

        network = SUPPORTED_NETWORKS[account.chainId] || SUPPORTED_NETWORKS[1];
        console.log('THOR Wallet connected successfully!');

      } else if (walletType === 'walletconnect' || walletType === WalletType.WALLETCONNECT) {
        console.log('Connecting to WalletConnect...');
        const walletConnect = new WalletConnectService();
        await walletConnect.connect();

        // WalletConnect implementation pending
        account = {
          address: '',
          balance: '0',
          chainId: 1,
        };
        network = SUPPORTED_NETWORKS[1];
      } else {
        console.log('Unsupported wallet type:', type);
        throw new Error('Unsupported wallet type');
      }

      console.log('Dispatching SET_CONNECTED');
      dispatch({
        type: 'SET_CONNECTED',
        payload: { account, network, walletType: type },
      });

      // Store connection in localStorage
      try {
        const connectionData = {
          type: String(type),
          address: account.address,
          chainId: account.chainId,
        };
        localStorage.setItem('walletConnection', JSON.stringify(connectionData));
        console.log('Saved to localStorage:', connectionData);
      } catch (storageError) {
        console.warn('Failed to save to localStorage:', storageError);
      }

      toast.success('Wallet connected successfully!');
    } catch (error) {
      console.error('WalletProvider: Connection error:', error);

      let errorMessage = 'Failed to connect wallet';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      console.error('Error message:', errorMessage);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
    }
  };

  const disconnectWallet = async () => {
    try {
      if (state.walletType === WalletType.WALLETCONNECT) {
        const walletConnect = new WalletConnectService();
        await walletConnect.disconnect();
      }

      dispatch({ type: 'SET_DISCONNECTED' });
      localStorage.removeItem('walletConnection');
      toast.info('Wallet disconnected');
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message });
      toast.error((error as Error).message);
    }
  };

  const switchNetwork = async (chainId: number) => {
    try {
      if (state.walletType === WalletType.METAMASK || state.walletType === WalletType.THOR_WALLET) {
        const provider = window.ethereum;
        if (!provider) {
          throw new Error('No wallet provider found');
        }

        const chainIdHex = `0x${chainId.toString(16)}`;

        try {
          await provider.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: chainIdHex }],
          });
        } catch (switchError: any) {
          // Chain not added, try to add it
          if (switchError.code === 4902) {
            const networkConfig = SUPPORTED_NETWORKS[chainId];
            if (networkConfig) {
              await provider.request({
                method: 'wallet_addEthereumChain',
                params: [{
                  chainId: chainIdHex,
                  chainName: networkConfig.name,
                  nativeCurrency: {
                    name: networkConfig.symbol,
                    symbol: networkConfig.symbol,
                    decimals: networkConfig.decimals,
                  },
                  rpcUrls: [networkConfig.rpcUrl],
                  blockExplorerUrls: [networkConfig.blockExplorerUrl],
                }],
              });
            }
          } else {
            throw switchError;
          }
        }

        const network = SUPPORTED_NETWORKS[chainId];
        if (network) {
          dispatch({ type: 'SET_NETWORK', payload: network });
          toast.success(`Switched to ${network.name}`);
        }
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message });
      toast.error((error as Error).message);
    }
  };

  const signMessage = async (message: string): Promise<string> => {
    if (!state.isConnected || !state.walletType) {
      throw new Error('Wallet not connected');
    }

    const provider = window.ethereum;
    if (!provider) {
      throw new Error('No wallet provider found');
    }

    const signature = await provider.request({
      method: 'personal_sign',
      params: [message, state.account?.address],
    });

    return signature;
  };

  const sendTransaction = async (params: any): Promise<string> => {
    if (!state.isConnected || !state.walletType) {
      throw new Error('Wallet not connected');
    }

    const provider = window.ethereum;
    if (!provider) {
      throw new Error('No wallet provider found');
    }

    const txHash = await provider.request({
      method: 'eth_sendTransaction',
      params: [{
        from: state.account?.address,
        ...params,
      }],
    });

    return txHash;
  };

  // Auto-reconnect on page load
  useEffect(() => {
    const savedConnection = localStorage.getItem('walletConnection');
    if (savedConnection) {
      try {
        console.log('Auto-reconnect: Found saved connection');
        const { type } = JSON.parse(savedConnection);

        // Clear for fresh start - uncomment connectWallet for auto-reconnect
        localStorage.removeItem('walletConnection');
        // connectWallet(type);
      } catch (error) {
        console.error('Failed to restore wallet connection:', error);
        localStorage.removeItem('walletConnection');
      }
    }
  }, []);

  // Setup wallet event listeners
  useEffect(() => {
    if ((state.walletType === WalletType.METAMASK || state.walletType === WalletType.THOR_WALLET) && state.isConnected) {
      const provider = window.ethereum;
      if (!provider) return;

      const handleAccountsChanged = (accounts: string[]) => {
        if (accounts.length === 0) {
          disconnectWallet();
          toast.info('Wallet disconnected');
        } else if (accounts[0] !== state.account?.address) {
          toast.info('Account changed');
          if (state.walletType) {
            connectWallet(state.walletType);
          }
        }
      };

      const handleChainChanged = (newChainId: string) => {
        const chainId = parseInt(newChainId, 16);
        const network = SUPPORTED_NETWORKS[chainId];
        if (network) {
          dispatch({ type: 'SET_NETWORK', payload: network });
          if (state.account) {
            dispatch({
              type: 'SET_ACCOUNT',
              payload: { ...state.account, chainId },
            });
          }
          toast.info(`Switched to ${network.name}`);
        } else {
          toast.warning('Switched to unsupported network');
        }
      };

      provider.on('accountsChanged', handleAccountsChanged);
      provider.on('chainChanged', handleChainChanged);

      return () => {
        provider.removeListener('accountsChanged', handleAccountsChanged);
        provider.removeListener('chainChanged', handleChainChanged);
      };
    }
  }, [state.walletType, state.isConnected, state.account?.address]);

  const contextValue: WalletContextType = {
    ...state,
    connectWallet,
    disconnectWallet,
    switchNetwork,
    signMessage,
    sendTransaction,
  };

  return <WalletContext.Provider value={contextValue}>{children}</WalletContext.Provider>;
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
}
